import {
  badgeClasses,
  inputAdornmentClasses,
  inputBaseClasses,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import {stepLabelClasses} from '@mui/material/StepLabel';
import {createTheme} from '@mui/material/styles';
import {gridClasses} from '@mui/x-data-grid-pro';
import {enUS, nbNO} from '@mui/x-data-grid-pro/locales';
import '@mui/x-data-grid-pro/themeAugmentation';
import '@mui/x-data-grid/themeAugmentation';
import '@mui/x-date-pickers-pro/themeAugmentation';
import {treeItemClasses} from '@mui/x-tree-view';
import '@mui/x-tree-view/themeAugmentation';
import type {Locale} from 'localization/types';
import {MasterLocale} from 'localization/types';
import {DropDownIcon} from 'components/icons/DropDownIcon';
import {ColorPalette} from './colorPalette';
import {Fonts, LabelFont} from './fonts';
import {px2rem} from './px2rem';
import {UXUnit} from './settings';

export const createCustomTheme = (locale: Locale | undefined = MasterLocale) =>
  createTheme(
    {
      breakpoints: {
        values: {
          xs: 0,
          sm: 1040,
          md: 1200,
          lg: 2000,
          xl: 2600,
        },
      },
      typography: {
        fontFamily: ['ABCMonumentGrotesk', 'sans-serif'].join(','),
        ...Fonts,
      },
      spacing: UXUnit,
      palette: {
        background: {
          default: '#EAEADE',
        },
        primary: {
          main: ColorPalette.burgundy,
          light: ColorPalette.offWhite,
          dark: ColorPalette.burgundy,
          contrastText: ColorPalette.offWhite,
        },
        secondary: {
          main: ColorPalette.textSecondary, //  ColorPalette.lightBeige,
          light: ColorPalette.offWhite,
          dark: '#226', // ColorPalette.brown,
          contrastText: ColorPalette.burgundy,
        },
        text: {
          primary: ColorPalette.textPrimary,
          secondary: ColorPalette.textSecondary,
          disabled: ColorPalette.burgundy50,
        },
        info: {
          main: ColorPalette.info40,
        },
        error: {
          main: ColorPalette.error,
        },
        warning: {
          main: ColorPalette.warning,
        },
        success: {
          main: ColorPalette.success,
        },
      },
      components: {
        MuiDesktopDatePicker: {
          defaultProps: {
            slotProps: {
              textField: {
                size: 'small',
                fullWidth: true,
                slotProps: {htmlInput: {autoComplete: 'off'}},
              },
            },
          },
        },
        MuiSimpleTreeView: {
          defaultProps: {
            itemChildrenIndentation: 0,
          },

          styleOverrides: {
            root: {
              flexGrow: 1,
              flex: 1,
              marginRight: 2,
              '& .MuiCollapse-root': {
                // Indentation per level
                paddingLeft: UXUnit * 4,
              },
              [`& .${treeItemClasses.iconContainer}`]: {
                '& svg': {
                  fontSize: '24px',
                },
              },
              [`& .${treeItemClasses.label}`]: {
                '& .labelBox': {
                  display: 'flex',
                  alignItems: 'center',
                  padding: '2px 0',
                  minHeight: '42px',
                  '& .outerBox': {
                    margin: '3px 0 3px 3px',
                    borderRadius: 8,
                    backgroundColor: ColorPalette.secondary.beige,
                    '& .nodeCode': {
                      padding: '4px 8px',
                      color: ColorPalette.burgundy,
                    },
                  },
                  '& .nodeText': {
                    paddingLeft: '4px',
                    color: ColorPalette.primary.burgundy,
                  },
                  '& .nodeStatus': {
                    marginLeft: 'auto',
                    color: 'inherit',
                  },
                },
              },
              [`& .${treeItemClasses.content}`]: {
                marginTop: '4px',
                borderRadius: 4,
                flex: 1,
                backgroundColor: `${ColorPalette.beige50}`,
                '&:hover': {
                  backgroundColor: `${ColorPalette.enhancedLightBeige}`,
                },
              },
              [`& [data-selected]`]: {
                backgroundColor: `${ColorPalette.secondary.beige}!important`,
                [`& .${treeItemClasses.content}`]: {
                  backgroundColor: `${ColorPalette.secondary.beige}!important`,
                  '&:hover': {
                    backgroundColor: `${ColorPalette.secondary.beige}!important`,
                  },
                  '&:focus': {
                    backgroundColor: `${ColorPalette.secondary.beige}!important`,
                  },
                },
              },
            },
          },
        },
        MuiDataGrid: {
          styleOverrides: {
            root: {
              [`& .${gridClasses.row}`]: {
                '--rowBorderColor': 'none',
                border: '0 solid white',
                outline: '1px solid white',
                borderRadius: ' 4px',
                backgroundColor: '#f9faf8',
              },
              [`& .${gridClasses.cell}`]: {
                color: '#480905',
                fontStyle: 'normal',
                fontWeight: 'normal',
                fontSize: '11px',
                lineHeight: '15px',
              },
              /* Replace selected header outline with weak/strong color of title */
              [`& .${gridClasses.columnHeader}`]: {
                color: '#9a9d90',
                fontStyle: 'normal',
                fontWeight: 'normal',
                fontSize: '9px',
                lineHeight: '9px',
                letterSpacing: '0.399561px',
                textTransform: 'uppercase',
              },
              /* Hide separator */
              [`& .${gridClasses.columnSeparator}`]: {
                color: 'white',
              },
              /* Remove cell bottom borders */
              [`& .${gridClasses.columnHeaders}, .${gridClasses.columnHeader}, .${gridClasses.cell}, .${gridClasses.filler}`]:
                {
                  borderBottomWidth: '0 !important',
                },
              [`& .${gridClasses.toolbar}`]: {
                borderBottom: 'none',
              },
              [`& .${gridClasses.toolbarContainer} .${badgeClasses.badge}`]: {
                backgroundColor: '#480905',
                fontSize: '.6em',
              },
              [`& .${gridClasses.footerContainer}`]: {
                borderTop: 'none',
              },

              [`& .${gridClasses['columnHeader--sorted']}, .${gridClasses.columnHeader}:hover, .${gridClasses.columnHeader}:focus, .${gridClasses.cell}, .${gridClasses.cell}:focus`]:
                {
                  outline: 'solid transparent 0 !important',
                  color: '#480905',
                },
              [`& .${gridClasses.columnHeaderTitleContainer} > .${gridClasses.iconButtonContainer}`]:
                {
                  visibility: 'collapse !important',
                  width: '0 !important',
                },
              [`& .${gridClasses.columnHeaderTitleContainer} .${gridClasses.columnHeaderTitle}`]:
                {
                  zIndex: 999,
                },
            },
          },
        },
        MuiBadge: {
          styleOverrides: {
            badge: {
              background: ColorPalette.offWhite,
              ...Fonts.body2,
            },
          },
        },
        MuiToolbar: {
          styleOverrides: {
            root: {
              '@media (min-width: 0px)': {
                minHeight: px2rem(46),
              },
            },
          },
        },
        MuiButton: {
          defaultProps: {
            disableRipple: true,
            disableElevation: true,
            size: 'medium',
            variant: 'contained',
            loadingPosition: 'start',
          },
          styleOverrides: {
            root: {
              variants: [
                {
                  props: {variant: 'contained'},
                  style: {
                    borderRadius: 999,
                    borderStyle: 'solid',
                    borderWidth: '1.5px',
                    borderColor: ColorPalette.terracotta,
                  },
                },
                {
                  props: {variant: 'contained', color: 'success'},
                  style: {
                    backgroundColor: ColorPalette.green2,
                    '&.Mui-disabled': {
                      backgroundColor: ColorPalette.green2,
                    },
                  },
                },
                {
                  props: {variant: 'contained', color: 'primary'},
                  style: {
                    backgroundColor: ColorPalette.offWhite,
                    color: ColorPalette.primary.burgundy,
                    '&.Mui-disabled': {
                      backgroundColor: ColorPalette.offWhite,
                    },
                  },
                },
                {
                  props: {variant: 'contained', color: 'error'},
                  style: {
                    backgroundColor: ColorPalette.brown,
                    color: ColorPalette.primary.burgundy,
                    '&.Mui-disabled': {
                      backgroundColor: ColorPalette.brown,
                      opacity: 0.8,
                    },
                  },
                },
                {
                  props: {size: 'medium'},
                  style: {
                    padding: `0 ${3 * UXUnit}px`,
                    height: `${5.83 * UXUnit}px`,
                  },
                },
              ],
              '& .MuiSvgIcon-fontSizeMedium': {
                fontSize: '1.5rem',
              },
            },
          },
        },
        MuiIconButton: {
          defaultProps: {
            loadingIndicator: <CircularProgress />,
          },
          styleOverrides: {
            root: {
              ':hover': {
                borderRadius: '16px',
              },
              borderRadius: '16px',
            },
          },
        },
        MuiFormLabel: {
          styleOverrides: {
            root: {
              paddingBottom: `${UXUnit}px!important`,
              ...LabelFont,
              color: ColorPalette.secondary.text,

              [`&.MuiFormLabel-colorWarning`]: {
                color: ColorPalette.warning,
                fontWeight: 'bold',
              },
              [`&.MuiFormLabel-colorError`]: {
                color: ColorPalette.error,
                fontWeight: 'bold',
              },
            },
          },
        },
        // Metadata input styling
        // @deprecated
        MuiInputLabel: {
          styleOverrides: {
            root: {
              paddingBottom: `${UXUnit}px!important`,
              ...LabelFont,
              color: ColorPalette.secondary.text,
            },
          },
        },
        // Metadata input styling
        MuiInputBase: {
          styleOverrides: {
            root: {
              ...Fonts.body1,
              border: 'none',
              boxShadow: `0 0 0 1.25px ${ColorPalette.primary.warmGreen}`,
              outline: 'none',
              transition: '.1s',
              borderRadius: UXUnit,
              ':focus-within, :hover': {
                boxShadow: `0 0 0 2px ${ColorPalette.primary.warmGreen}`,
              },
              [`&.MuiInputBase-colorWarning`]: {
                boxShadow: `0 0 0 1.25px ${ColorPalette.warning}`,
              },
              [`&.MuiInputBase-colorError`]: {
                boxShadow: `0 0 0 2px ${ColorPalette.error}`,
              },
              '&.Mui-error': {
                //boxShadow: `0 0 0 1.25px ${ColorPalette.error}`,
              },
              '&.Mui-error:focus-within, &.Mui-error:hover': {
                //boxShadow: `0 0 0 2px ${ColorPalette.error}`,
              },
              '&.MuiOutlinedInput-root': {
                '&.Mui-disabled': {
                  opacity: 0.6,
                },
              },
            },
            input: {
              color: ColorPalette.primary.burgundy,
              '::selection': {
                background: ColorPalette.primary.lightGreen,
              },
              '::placeholder': {
                color: ColorPalette.primary.warmGreen,
              },
            },
            [`&.${inputBaseClasses.input}.${inputBaseClasses.multiline}`]: {
              color: ColorPalette.primary.burgundy,
              padding: '0!important',
              '::selection': {
                background: ColorPalette.primary.lightGreen,
              },
            },
          },
        },
        MuiInputAdornment: {
          styleOverrides: {
            root: {
              [`&.${inputAdornmentClasses.positionEnd}`]: {
                marginLeft: '0',
              },
            },
          },
        },
        MuiCheckbox: {
          styleOverrides: {
            root: {
              // Same padding t/r/b/l to have symmetric, circular checkbox hover
              padding: '3px!important',
              // Use margins to adjust position
              margin: '0 0 0 2px!important',
            },
          },
        },
        MuiRadio: {
          styleOverrides: {
            root: {
              // Same padding t/r/b/l to have symmetric, circular hover
              padding: '4px!important',
              // Use margins to adjust position
              margin: '0 2px 0 4px!important',
            },
          },
        },
        // Metadata input styling
        MuiFormControl: {
          styleOverrides: {
            root: {
              background: ColorPalette.primary.white,
            },
          },
        },
        MuiFormControlLabel: {
          styleOverrides: {
            label: {
              ...LabelFont,
            },
          },
        },
        // Metadata input styling
        MuiOutlinedInput: {
          styleOverrides: {
            notchedOutline: {
              border: 'none',
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: {
              ...Fonts.body1,
              borderRadius: `${UXUnit}px`,
            },
            label: {
              overflow: 'visible',
            },
          },
        },
        MuiAvatar: {
          styleOverrides: {
            root: {
              // Hard code 24px to match icon sizes in svg
              width: '24px', // px2rem(24),
              height: '24px', // px2rem(24),
              background: 'transparent',
            },
          },
        },
        MuiAccordion: {
          defaultProps: {
            disableGutters: true,
          },
          styleOverrides: {
            root: {
              background: ColorPalette.secondary.lightBeige,
              boxShadow: 'none',
              borderRadius: `${2 * UXUnit}px`,

              '&:before': {
                content: 'none',
              },

              '&.Mui-expanded': {
                margin: 0,
              },
              '&:not(:last-of-type)': {
                marginBottom: `${2 * UXUnit}px`,
              },
            },
          },
        },
        MuiAccordionSummary: {
          defaultProps: {
            expandIcon: <DropDownIcon />,
          },
          styleOverrides: {
            root: {
              minHeight: 6.5 * UXUnit,
              padding: 0,
              paddingLeft: 6.5 * UXUnit,

              '&:before': {
                content: 'none',
              },
            },
            content: {
              margin: 0,
            },
            expandIconWrapper: {
              position: 'absolute',
              left: 2 * UXUnit,
              display: 'flex',
              alignItems: 'center',
              background: ColorPalette.beige,
              borderRadius: `${2 * UXUnit}px`,

              '&:hover': {
                opacity: 0.8,
                background: ColorPalette.enhancedBeige,
              },
            },
          },
        },
        MuiTextField: {
          styleOverrides: {
            root: {
              borderRadius: `${UXUnit}px`,
            },
          },
        },
        MuiStep: {
          styleOverrides: {
            root: {
              marginRight: `${2 * UXUnit}px`,
              padding: 0,
            },
          },
        },
        MuiStepButton: {
          styleOverrides: {
            root: {
              margin: 0,
              padding: 0,
            },
          },
        },
        MuiStepLabel: {
          styleOverrides: {
            iconContainer: {
              padding: 0,
              visibility: 'hidden',
              width: 0,
            },
            label: {
              padding: `${px2rem(6)} ${px2rem(12)}`,
              borderRadius: 9999,
              [`&.${stepLabelClasses.active}`]: {
                backgroundColor: ColorPalette.burgundy,
                color: ColorPalette.offWhite,
              },
            },
          },
        },
        MuiTooltip: {
          styleOverrides: {
            tooltip: {
              ...Fonts.body2,
              backgroundColor: ColorPalette.primary.burgundy,
              color: ColorPalette.offWhite,
              borderRadius: `${UXUnit}px`,
              padding: `${UXUnit}px ${2 * UXUnit}px`,
            },
            arrow: {
              color: ColorPalette.primary.burgundy,
            },
          },
          defaultProps: {
            arrow: true,
          },
        },
        MuiSnackbar: {
          styleOverrides: {
            root: {
              top: `${4 * UXUnit}px`,
              borderRadius: `${4 * UXUnit}px`,
            },
          },
        },
        MuiAlert: {
          styleOverrides: {
            root: {
              borderRadius: `${4 * UXUnit}px`,
            },
          },
          variants: [
            {
              props: {severity: 'success'},
              style: {
                backgroundColor: ColorPalette.success,
              },
            },
            {
              props: {severity: 'error'},
              style: {
                backgroundColor: ColorPalette.error,
              },
            },
            {
              props: {severity: 'warning'},
              style: {
                backgroundColor: ColorPalette.warmGreen,
              },
            },
          ],
        },
      },
      shape: {
        borderRadius: UXUnit,
      },
    },
    locale === 'en' ? enUS : nbNO,
  );
